import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
  ValidatorFn,
} from '@angular/forms';
import {
  DialogService,
  DynamicDialogComponent,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { user } from 'src/app/models/user.model';
import { NotificationService } from 'src/app/services/notification.service';
import {
  LobbyBypassScope,
  MeetingParticipantInfo,
  OnlineMeeting,
  OnlineMeetingPresenters,
} from '@microsoft/microsoft-graph-types';
import { AccordionModule } from 'primeng/accordion';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule } from 'primeng/table';
import { Subject, takeUntil } from 'rxjs';
import { ReadonlyFloatLabel } from '../../readonly-float-label/readonly-float-label';
import { TeamsDocentMeetingService } from 'src/app/services/teams-docent-meeting.service';
import { TeamsCourseMeetingService } from 'src/app/services/teams-course-meeting.service';
import { MeetingCrudService } from 'src/app/models/meeting-crud-service.model';
import { notifyMeetingCreationResult } from 'src/app/utils/meeting-creation-summary';
import { MeetingServiceType } from 'src/app/models/meeting-service-type.model';
import { Course } from 'src/app/models/course.model';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-teams-meeting-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    CalendarModule,
    FloatLabelModule,
    InputTextModule,
    InputTextareaModule,
    MultiSelectModule,
    CheckboxModule,
    DropdownModule,
    AccordionModule,
    TooltipModule,
    TableModule,
    ConfirmDialogModule,
    ReadonlyFloatLabel,
  ],
  providers: [ConfirmationService],
  templateUrl: './teams-meeting-dialog.html',
  styleUrl: './teams-meeting-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamsMeetingDialog implements OnInit, OnDestroy {
  instance?: DynamicDialogComponent;
  private docentName?: string;
  private courseName?: string;
  meeting?: OnlineMeeting;
  meetingForm!: FormGroup;
  users: user[] = [];

  /** Lobby Bypass Settings for MS Graph */
  lobbyBypassSettings: { label: string; value: LobbyBypassScope }[] = [
    { label: 'Jeder', value: 'everyone' },
    { label: 'Organisationsmitglieder', value: 'organization' },
    { label: 'Nur Organisatoren', value: 'organizer' },
  ];

  /** Presenter-Einstellungen für MS Graph 
  Bestimmt, wer in der Besprechung als Präsentator agieren darf; richtige Wahl: 'Nur Organisatoren', dann können Dozenten die Präsentatorenrolle dynamisch zuweisen
  */
  allowedPresentersSettings: {
    label: string;
    value: OnlineMeetingPresenters;
  }[] = [
    { label: 'Jeder', value: 'everyone' },
    { label: 'Organisationsmitglieder', value: 'organization' },
    { label: 'Nach Rolle', value: 'roleIsPresenter' },
    { label: 'Nur Organisatoren', value: 'organizer' },
  ];

  /** Tracks form submission state. Prevents multiple submissions. */
  isSubmitting: boolean = false;
  /**
   * Das Accordion wird erst nach dem ersten Frame erzeugt. Sonst blitzt sein Inhalt beim Öffnen
   * des Dialogs kurz auf: PrimeNG legt ihn im DOM an, bevor der eingeklappte Animationszustand
   * greift. Eine kürzere transitionOptions-Dauer allein genügt dagegen nicht.
   */
  showAdvancedSettings = false;
  /** Set to true as soon as submit is clicked once. */
  private validate: boolean = false;
  private destroy$: Subject<boolean> = new Subject();
  private meetingServiceType: MeetingServiceType;

  /** Beim Anlegen ist der Titel eine Vorlage für einen Stapel, kein einzelner Betreff. */
  get isNewDocentMeeting(): boolean {
    return this.meetingServiceType === 'docent' && !this.meeting;
  }

  // Beim Anlegen zählt nur die Differenz (MeetingLifetime), beim Bearbeiten gehen beide Werte an Graph.
  get startDateTimeTooltip(): string {
    return this.isNewDocentMeeting
      ? 'Dient nur zur Berechnung der Meeting-Dauer. Der hier gewählte Tag und die Uhrzeit werden nicht übernommen.'
      : 'Datum und Uhrzeit, zu der das Meeting beginnen soll. Das Meeting bleibt für 60 Tage aktiv. Dieser Timer wird zurückgesetzt, wenn das Meeting betreten oder aktualisiert wird.';
  }

  get endDateTimeTooltip(): string {
    return this.isNewDocentMeeting
      ? 'Dient nur zur Berechnung der Meeting-Dauer. Der Abstand zum Startzeitpunkt bestimmt, wie lange jedes Tagesmeeting läuft.'
      : 'Datum und Uhrzeit, zu der das Meeting enden soll. Das Meeting bleibt für 60 Tage aktiv. Dieser Timer wird zurückgesetzt, wenn das Meeting betreten oder aktualisiert wird.';
  }

  constructor(
    private ref: DynamicDialogRef,
    private readonly dialogService: DialogService,
    private readonly teamsDocentMeetingService: TeamsDocentMeetingService,
    private readonly teamsCourseMeetingService: TeamsCourseMeetingService,
    private readonly notificationService: NotificationService,
    private readonly confirmationService: ConfirmationService,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.instance = this.dialogService.getInstance(this.ref);
    if (this.instance && this.instance.data) {
      this.docentName = this.instance.data['docentName'];
      this.courseName = this.instance.data['courseName'];
      this.meeting = this.instance.data['meeting'];
      this.users = this.instance.data['users'];
      this.meetingServiceType = this.instance.data['meetingServiceType'];
      if (this.meetingServiceType === 'docent' && !this.docentName) {
        throw new Error(
          'MeetingServiceType ist "docent", aber kein User übergeben',
        );
      } else if (this.meetingServiceType === 'course' && !this.courseName) {
        throw new Error(
          'MeetingServiceType ist "course", aber kein courseName übergeben',
        );
      }
    }
  }

  ngOnInit(): void {
    this.initializeForm();
    setTimeout(() => {
      this.showAdvancedSettings = true;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    // Emit a value to complete any active subscriptions
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private readonly dateRangeValidator = (group: FormGroup) => {
    const start = group.get('startDateTime')?.value;
    const end = group.get('endDateTime')?.value;
    if (!start || !end) return null;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diff = endDate.getTime() - startDate.getTime();
    const isDurationTooShort = diff < 60 * 1000; // Minimum duration of 1 minute
    const isDurationExceedsMax = diff > 60 * 24 * 60 * 60 * 1000; // 60 days is the maximum retention period for online meetings in Microsoft Graph
    // Ensure end date is after start date and that the difference is minimum 1 minute and maximum 60 days
    if (isDurationTooShort) {
      return {
        endBeforeOrEqualStart: isDurationTooShort,
      };
    } else if (isDurationExceedsMax) {
      return { invalidDuration: true };
    }

    return null;
  };

  private readonly lecturerEmailValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const participantEmails = control.value as string[] | null;
    if (!participantEmails?.length) {
      return null;
    }

    const allowedEmailRules = (environment.validLecturerEmails ?? [])
      .map((rule) => rule.trim().toLowerCase())
      .filter(Boolean);

    if (!allowedEmailRules.length) {
      return null;
    }

    const invalidEmails = participantEmails.filter(
      (email) => !this.isAllowedLecturerEmail(email, allowedEmailRules),
    );

    return invalidEmails.length
      ? {
          invalidLecturerEmails: {
            invalidEmails,
            allowedEmailRules,
          },
        }
      : null;
  };

  private isAllowedLecturerEmail(
    email: string,
    allowedRules: string[],
  ): boolean {
    const normalizedEmail = email?.trim().toLowerCase();
    if (!normalizedEmail) {
      return false;
    }

    return allowedRules.some((rule) =>
      rule.startsWith('@')
        ? normalizedEmail.endsWith(rule)
        : normalizedEmail === rule,
    );
  }

  /**
   * Vorbelegung eines Regelschultags (08:00 bis 14:30) am heutigen Tag. Beim
   * Dozenten-Stapel zählt ohnehin nur die Differenz, beim Einzelmeeting ist der
   * Schultag die naheliegende Annahme.
   */
  private defaultDateTime(hours: number, minutes: number): Date {
    const value = new Date();
    value.setHours(hours, minutes, 0, 0);
    return value;
  }

  // Beim Anlegen wird der Dozent als Organizer vorbelegt, über den Namen aus der Nutzerliste.
  private defaultParticipants(): string[] {
    const name = this.docentName?.trim().toLowerCase();
    const email = name
      ? this.users?.find((u) => u.fullName?.trim().toLowerCase() === name)?.email
      : undefined;
    return email ? [email] : [];
  }

  private initializeForm(): void {
    const subject =
      this.meetingServiceType === 'docent'
        ? this.docentName
          ? `Kurs von ${this.docentName}`
          : ''
        : this.courseName
          ? `${this.courseName.trim()} Selbststudium`
          : '';
    // Wenn kein Meeting übergeben wurde, initialisiere das Formular mit Standardwerten
    if (!this.meeting) {
      this.meetingForm = this.fb.group(
        {
          subject: [subject, Validators.required],
          startDateTime: [this.defaultDateTime(8, 0), Validators.required],
          endDateTime: [this.defaultDateTime(14, 30), Validators.required],
          participants: [
            this.defaultParticipants(),
            [Validators.required, this.lecturerEmailValidator],
          ],
          allowAttendeeToEnableCamera: [true],
          allowAttendeeToEnableMic: [true],
          allowMeetingChat: [true],
          shareMeetingChatHistory: [true],
          allowTeamworkReactions: [true],
          recordAutomatically: [true],
          lobbyBypassSettings: ['organizer'],
          allowedPresenters: ['organizer'],
        },
        { validators: this.dateRangeValidator },
      );
    } else {
      // Wenn ein Meeting übergeben wurde, initialisiere das Formular mit den Meeting-Daten,
      // lasse aber Felder, die laut Graph API nicht aktualisiert werden dürfen weg
      // (Hier: shareMeetingChatHistoryDefault)
      this.meetingForm = this.fb.group(
        {
          subject: [this.meeting?.subject || subject, Validators.required],
          startDateTime: [
            this.meeting ? new Date(this.meeting.startDateTime) : null,
            Validators.required,
          ],
          endDateTime: [
            this.meeting ? new Date(this.meeting.endDateTime) : null,
            Validators.required,
          ],
          expirationDateTime: [
            this.meeting['expiryDateTime']?.toString()
              ? new Date(this.meeting['expiryDateTime'])
              : null,
          ],
          participants: [
            this.meeting?.participants?.attendees?.map((a) => a.upn) || [],
            [Validators.required, this.lecturerEmailValidator],
          ],
          allowAttendeeToEnableCamera: [
            this.meeting?.allowAttendeeToEnableCamera ?? true,
          ],
          allowAttendeeToEnableMic: [
            this.meeting?.allowAttendeeToEnableMic ?? true,
          ],
          allowMeetingChat: [
            this.meeting?.allowMeetingChat?.toLowerCase() === 'enabled',
          ],
          allowTeamworkReactions: [
            this.meeting?.allowTeamworkReactions ?? true,
          ],
          recordAutomatically: [this.meeting?.recordAutomatically ?? true],
          lobbyBypassSettings: [
            this.meeting?.lobbyBypassSettings?.scope?.toLowerCase() ||
              'organizer',
          ],
          allowedPresenters: [
            this.allowedPresentersSettings.find(
              (option) =>
                option.value.toLowerCase() ===
                this.meeting?.allowedPresenters?.toLowerCase(),
            )?.value || 'organizer',
          ],
        },
        { validators: this.dateRangeValidator },
      );
    }
  }

  isInvalid(controlName: string) {
    const control = this.meetingForm.get(controlName);
    return (
      control?.invalid && (control.touched || control.dirty || this.validate)
    );
  }

  onSubmit(): void {
    if (this.isSubmitting) {
      return; // Prevent multiple submissions
    }

    this.validate = true; // Trigger validation messages even for untouched fields
    if (!this.meetingForm.valid) {
      const participantErrors = this.meetingForm.get('participants')?.errors;
      if (participantErrors?.['invalidLecturerEmails']) {
        this.notificationService.error(
          `Ungültige Organizer-E-Mail-Adresse. Erlaubt sind nur: ${participantErrors['invalidLecturerEmails'].allowedEmailRules.join(', ')}`,
        );
        return;
      }

      this.notificationService.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    this.isSubmitting = true;
    const formValue = this.meetingForm.value;

    if (this.meeting) {
      const meetingData: OnlineMeeting =
        this.createOnlineMeetingUpdateRequest(formValue);
      this.updateOnlineMeeting(meetingData);
    } else {
      const meetingData: OnlineMeeting =
        this.createOnlineMeetingCreateRequest(formValue);
      this.createOnlineMeeting(meetingData);
    }
  }

  private createOnlineMeetingCreateRequest(formValue: any): OnlineMeeting {
    return {
      ...this.createOnlineMeetingUpdateRequest(formValue),
      shareMeetingChatHistoryDefault: formValue.shareMeetingChatHistory
        ? 'all'
        : 'none',
    };
  }

  private createOnlineMeetingUpdateRequest(formValue: any): OnlineMeeting {
    return {
      subject: formValue.subject,
      startDateTime: formValue.startDateTime,
      endDateTime: formValue.endDateTime,
      participants: {
        attendees: formValue.participants.map(
          (email: string) =>
            ({
              upn: email,
              role: 'coorganizer',
            }) as MeetingParticipantInfo,
        ),
      },
      allowAttendeeToEnableCamera: formValue.allowAttendeeToEnableCamera,
      allowAttendeeToEnableMic: formValue.allowAttendeeToEnableMic,
      allowMeetingChat: formValue.allowMeetingChat ? 'enabled' : 'disabled',
      allowTeamworkReactions: formValue.allowTeamworkReactions,
      recordAutomatically: formValue.recordAutomatically,
      lobbyBypassSettings: {
        scope: formValue.lobbyBypassSettings,
      },
      allowedPresenters: formValue.allowedPresenters,
    };
  }

  private getMeetingService(): MeetingCrudService {
    switch (this.meetingServiceType) {
      case 'docent':
        return this.teamsDocentMeetingService;
      case 'course':
        return this.teamsCourseMeetingService;
      default:
        throw new Error(
          `Ungültiger MeetingServiceType: ${this.meetingServiceType}`,
        );
    }
  }

  private updateOnlineMeeting(meetingData: OnlineMeeting) {
    this.getMeetingService()
      .update(this.meeting.id, meetingData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.notificationService.success(
            'Teams Meeting erfolgreich aktualisiert',
          );
          this.ref.close(response);
          // No need to reset isSubmitting here since the dialog will close
        },
        error: (error) => {
          this.notificationService.error(
            'Fehler beim Aktualisieren des Meetings',
            error,
          );
          this.isSubmitting = false;
        },
      });
  }

  // Dozent: Stapel Tagesmeetings mit Laufergebnis. Selbststudium: genau ein Meeting.
  private createOnlineMeeting(meetingData: OnlineMeeting) {
    if (this.meetingServiceType === 'docent') {
      this.createDocentMeetings(meetingData);
    } else {
      this.createCourseMeeting(meetingData);
    }
  }

  private createDocentMeetings(meetingData: OnlineMeeting) {
    this.teamsDocentMeetingService
      .create(meetingData, this.docentName.trim())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          notifyMeetingCreationResult(this.notificationService, result);
          this.ref.close(result);
        },
        error: (error) => this.handleCreationError(error),
      });
  }

  private createCourseMeeting(meetingData: OnlineMeeting) {
    this.teamsCourseMeetingService
      .create(meetingData, this.courseName.trim())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.notificationService.success(
            'Teams Meeting erfolgreich erstellt',
          );
          this.ref.close(response);
        },
        error: (error) => this.handleCreationError(error),
      });
  }

  private handleCreationError(error: unknown) {
    const httpError = error as { error?: { message?: string } };
    this.notificationService.error(
      httpError?.error?.message ?? 'Fehler beim Erstellen des Meetings',
      error,
    );
    this.isSubmitting = false;
  }

  onCancel(): void {
    this.ref.close();
  }

  // Löscht bei Teams und in der DB, nicht rückgängig zu machen — daher die Rückfrage.
  deleteTeamsMeeting(event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    if (!this.meeting) {
      return;
    }

    this.confirmationService.confirm({
      header: 'Löschen bestätigen',
      message: this.meeting.subject
        ? `Möchten Sie das Meeting "${this.meeting.subject}" wirklich löschen? Es wird auch bei Microsoft Teams entfernt.`
        : 'Möchten Sie dieses Meeting wirklich löschen? Es wird auch bei Microsoft Teams entfernt.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Ja',
      rejectLabel: 'Nein',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.executeDelete(),
    });
  }

  private executeDelete(): void {
    this.getMeetingService()
      .delete(this.meeting.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Teams-Meeting erfolgreich gelöscht');
          this.ref.close({});
        },
        error: (error) => {
          this.notificationService.error(
            'Fehler beim Löschen des Teams-Meetings',
            error,
          );
        },
      });
  }
}
